import type { FeedbackAnswerQuestion, QuestionData, QuestionExportAnswer } from '@repo/types/questions/Question';
import type { IQuestion } from '@repo/types/questions/Question.interface';
import type { QuizAttemptPrivate, QuizPrivate, QuizSection } from '@repo/types/quiz';

import { QuestionFactory } from '@repo/lib/quiz/questions/class/QuestionFactory';
import { useMemo } from 'react';

import type { QuizResultStats } from '.';

const INITIAL_STATS: QuizResultStats = {
  attemptScore: 0,
  maxScore: 0,
  correct: 0,
  incorrect: 0,
  partially: 0,
  unmarked: 0,
  unanswered: 0,
};

export interface QuizResultStatsProps {
  quiz?: QuizPrivate;
  attempt?: QuizAttemptPrivate;
  selectedIdx: number | null;
  marked?: FeedbackAnswerQuestion[][] | null;
}

export function useQuizResultStats(quizResultStatsProps: QuizResultStatsProps): QuizResultStats {
  const { quiz, attempt, selectedIdx, marked } = quizResultStatsProps;

  return useMemo<QuizResultStats>(() => {
    if (!quiz || !attempt) return { ...INITIAL_STATS };

    const qFactory = new QuestionFactory();
    const resolvedMarked: FeedbackAnswerQuestion[][] | undefined = marked ?? attempt.body.marked ?? undefined;

    const calcSectionStats = (section: QuizSection, markedSection: FeedbackAnswerQuestion[] | undefined, sectionIdx: number) => {
      const sum = {
        attemptScore: 0,
        correct: 0,
        incorrect: 0,
        partially: 0,
        unmarked: 0,
        unanswered: 0,
      };

      for (let i = 0; i < section.questions.length; i++) {
        const qData: QuestionData | undefined = section.questions[i];

        if (!qData) continue;

        const aData: QuestionExportAnswer | undefined = attempt.body.answers?.at(sectionIdx)?.at(i);
        const mData: FeedbackAnswerQuestion | undefined = markedSection?.[i];

        const qClass: IQuestion = qFactory.createQuestion(qData, null, mData);
        if (aData) qClass.importAnswer(aData);
        const state = qClass.correctState();

        if (state === 'waiting') {
          sum.unmarked += 1;
          continue;
        }

        sum.attemptScore += qClass.getPoints();

        if (!qClass.isAnswered()) sum.unanswered += 1;
        else if (state === true) sum.correct += 1;
        else if (state === false) sum.incorrect += 1;
        else if (state === 'partially') sum.partially += 1;
      }

      return sum;
    };

    if (selectedIdx == null) {
      const totals = {
        attemptScore: 0,
        correct: 0,
        incorrect: 0,
        partially: 0,
        unmarked: 0,
        unanswered: 0,
      };

      for (let i = 0; i < quiz.content.length; i++) {
        const section: QuizSection | undefined = quiz.content[i];

        if (!section) continue;

        const sectionStats = calcSectionStats(section, resolvedMarked?.at(i), i);
        totals.attemptScore += sectionStats.attemptScore;
        totals.correct += sectionStats.correct;
        totals.incorrect += sectionStats.incorrect;
        totals.partially += sectionStats.partially;
        totals.unanswered += sectionStats.unanswered;
        totals.unmarked += sectionStats.unmarked;
      }

      return {
        maxScore: quiz.points,
        ...totals,
      };
    }

    const section: QuizSection | undefined = quiz.content[selectedIdx];

    if (!section) return { ...INITIAL_STATS };

    const markedSection = resolvedMarked?.at(selectedIdx);
    const sectionStats = calcSectionStats(section, markedSection, selectedIdx);
    const maxScore: number = section.questions.reduce((sum, q) => sum + q.points, 0) ?? 0;

    return {
      ...sectionStats,
      maxScore,
    };
  }, [quiz, attempt, selectedIdx, marked]);
}
